import { startOfDay, isSameDay, addMinutes, endOfDay, areIntervalsOverlapping, differenceInMinutes, setHours, isBefore, addHours, } from "date-fns";
import { isDefined, uid, getDeepCopy } from "./helpers";
import { roundTimeByStep } from "./time";
import { explodeEvent, getLastDate, parseRecurring } from "./recurring";
import { toHSL, toHSLString } from "./colors";
export function normalizeEvent({ event, calendars, config: { timeStep, readonly, dragMove, dragResize }, }) {
    const copy = getDeepCopy(event);
    const defaultDate = new Date();
    const normalized = {
        readonly,
        dragMove,
        dragResize,
        id: event.id || uid(),
        ...copy,
    };
    if (normalized.status !== "cancelled" &&
        !normalized.start_date &&
        !normalized.end_date) {
        (normalized.start_date = defaultDate),
            (normalized.end_date = addMinutes(defaultDate, timeStep));
    }
    const { STDATE, DTEND, RRULE, recurring } = normalized;
    let type;
    if (normalized.type) {
        type = calendars.find(t => t.id === normalized.type);
    }
    normalized.type = type ? type.id : calendars[0].id;
    if (!normalized.start_date || !normalized.end_date)
        return normalized;
    const start_date = roundTimeByStep(new Date(normalized.start_date), timeStep);
    normalized.start_date = start_date;
    let end_date = roundTimeByStep(new Date(normalized.end_date), timeStep);
    if (start_date.valueOf() === end_date.valueOf()) {
        end_date = addMinutes(start_date, timeStep);
    }
    normalized.end_date = isBefore(end_date, start_date)
        ? addHours(start_date, 2)
        : end_date;
    if (RRULE) {
        normalized.recurring = recurring ?? true;
        normalized.STDATE = isDefined(STDATE) ? new Date(STDATE) : start_date;
        normalized.DTEND = isDefined(DTEND)
            ? new Date(DTEND)
            : getLastDate(parseRecurring(RRULE, normalized, undefined, false), normalized);
    }
    return normalized;
}
export function sortEvents(data) {
    return data.sort((a, b) => a.start_date.valueOf() - b.start_date.valueOf());
}
export function isMultiEvent(event) {
    const { start_date, end_date, allDay } = event;
    const delta = differenceInMinutes(end_date, start_date); // 23.59
    return delta >= 1439 || allDay || !isSameDay(start_date, end_date);
}
export function isEventInTimeRange(event, range) {
    const { start_date, end_date } = event;
    const start = setHours(start_date, range[0]);
    const end = setHours(start_date, range[1]);
    return areIntervalsOverlapping({ start: start_date, end: end_date }, { start, end });
}
export function isEventInDay(event, range) {
    const { start_date, end_date } = event;
    const [start, end] = range;
    return areIntervalsOverlapping({ start: start_date, end: end_date }, { start, end });
}
export function stackEvents(data) {
    let index = 0;
    let events = sortEvents(data);
    const result = {};
    while (events.length) {
        const IDs = [];
        let last = events[0];
        IDs.push(last.id);
        events.forEach(event => {
            if (event.start_date >= last.end_date) {
                IDs.push(event.id);
                last = event;
            }
        });
        events = events.filter(ev => !IDs.includes(ev.id));
        IDs.forEach(id => (result[id] = index));
        index++;
    }
    return result;
}
export function roundEvents(events) {
    return sortEvents(events).map(event => {
        const ev = { ...event };
        ev.start_date = startOfDay(ev.start_date);
        ev.end_date = isSameDay(ev.end_date, addMinutes(ev.end_date, -1))
            ? ev.end_date
            : endOfDay(new Date(addMinutes(ev.end_date, -1)));
        return ev;
    });
}
export function getEventStyles(node, config) {
    function setStyles(config) {
        const target = node;
        const { style } = target;
        const { event, calendars, backgroundFactor, dimPastEvents, isPast } = config;
        const { eventData, position = {
            top: "0",
            width: "100%",
            left: "0",
            height: "32px",
            zIndex: 0,
        }, } = event;
        const { top, left, width, height, hideEvent } = position;
        const type = calendars.find(calendar => calendar.id === eventData.type) ||
            calendars[0];
        let background = eventData.color?.[backgroundFactor] ||
            type.color[backgroundFactor];
        let borderColor = eventData.color?.border || type.color.border;
        let color = eventData.color?.textColor || type.color.textColor;
        if (dimPastEvents && isPast) {
            borderColor = dimColor(borderColor);
            background = dimColor(background, 90);
            color = "rgba(44, 47, 60, 0.5)";
        }
        style.setProperty("--wx-border-color", borderColor);
        style.top = top;
        style.left = left;
        style.width = width;
        style.height = height;
        style.color = color || null;
        if (background) {
            style.setProperty("--wx-background", background);
        }
        style.visibility = hideEvent ? "hidden" : "visible";
        style.opacity = hideEvent ? 0 : 1;
        style.zIndex = position.zIndex || 0;
        const { offsetHeight } = target;
        let lineHeight;
        let paddingLeft;
        ({ lineHeight, paddingLeft } = window.getComputedStyle(target)); // Padding from DayEvent .event-box ( paddingTop === paddingLeft)
        lineHeight = parseInt(lineHeight);
        paddingLeft = parseInt(paddingLeft);
        if (!lineHeight)
            return;
        if (offsetHeight <= lineHeight + paddingLeft &&
            !target.classList.contains("wx-event-calendar-event-box--small")) {
            target.classList.add("wx-event-calendar-event-box--small");
        }
        if (offsetHeight > lineHeight + paddingLeft) {
            style.fontSize = "var(--wx-font-size)";
            if (target.classList.contains("wx-event-calendar-event-box--small")) {
                target.classList.remove("wx-event-calendar-event-box--small");
            }
        }
        if (offsetHeight && offsetHeight <= lineHeight) {
            style.lineHeight = style.minHeight;
            style.fontSize = "var(--wx-font-size-sm)";
        }
    }
    setStyles(config);
    return {
        update: (nextConfig) => setStyles(nextConfig),
    };
}
export function dimColor(color, max = 80) {
    const hsl = toHSL(color, true);
    const l = hsl.l + 25;
    return toHSLString({
        h: hsl.h,
        s: hsl.s - 15,
        l: l > max ? max : l,
    });
}
export function getEventsByBounds(events, bounds, disabledCalendars, _dates) {
    const normalEvents = [];
    let recurringEvents = [];
    events.forEach(event => {
        const { start_date, end_date, type, recurring, STDATE, DTEND, status, recurringEventId, } = event;
        const [start, end] = bounds;
        const disabled = disabledCalendars.find(c => c.id === type);
        const isSeries = recurring && !recurringEventId;
        if (status !== "cancelled" &&
            !disabled &&
            !isSeries &&
            areIntervalsOverlapping({ start: start_date, end: end_date }, { start, end })) {
            normalEvents.push(event);
        }
        if (isSeries &&
            areIntervalsOverlapping({ start: STDATE, end: DTEND }, { start, end })) {
            const children = events.filter(e => e.recurringEventId === event.id);
            const r = explodeEvent(start, end, event, children, _dates);
            recurringEvents = [...recurringEvents, ...r];
        }
    });
    return [...normalEvents, ...recurringEvents];
}
//# sourceMappingURL=events.js.map